<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { catalogApi } from '$lib/api/catalog';
  import { getErrorMessage } from '$lib/api/client';

  let days = $state(90);

  const analyticsQuery = createQuery(() => ({
    queryKey: ['catalog-analytics', days],
    queryFn: () => catalogApi.analytics(days)
  }));

  function formatDate(iso: string): string {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
  }
</script>

<div class="mb-4 flex items-center gap-2">
  <div class="label !mb-0">Window</div>
  <select class="input !h-8 w-32" bind:value={days}>
    <option value={30}>30 days</option>
    <option value={90}>90 days</option>
    <option value={180}>180 days</option>
    <option value={365}>365 days</option>
  </select>
</div>

{#if analyticsQuery.isLoading}
  <p class="tym-loading-state">Loading analytics&hellip;</p>
{:else if analyticsQuery.isError}
  <p class="tym-error-state">{getErrorMessage(analyticsQuery.error)}</p>
{:else if analyticsQuery.data}
  {@const data = analyticsQuery.data}
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
    <div class="tym-card p-4">
      <h3 class="mb-3 font-heading text-[13.5px] font-bold text-navy">Fast movers (top 20)</h3>
      <div class="tym-table-wrap tym-table-scroll max-h-[360px]">
        <table class="tym-table">
          <thead>
            <tr>
              <th>Product</th>
              <th class="num w-[70px]">Qty used</th>
              <th class="w-[110px]">Last quoted</th>
            </tr>
          </thead>
          <tbody>
            {#each data.fast_movers as m (m.id)}
              <tr>
                <td>
                  <div class="text-[13px] font-medium text-navy leading-tight">{m.name}</div>
                  <div class="text-[11px] text-dark-gray font-mono mt-0.5">{m.part_number} &middot; {m.category}</div>
                </td>
                <td class="num tabular-nums">{m.count}</td>
                <td class="text-[12px] text-dark-gray">{formatDate(m.last_quoted)}</td>
              </tr>
            {:else}
              <tr><td class="empty" colspan="3">No usage in this window.</td></tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>

    <div class="tym-card p-4">
      <h3 class="mb-3 font-heading text-[13.5px] font-bold text-navy">
        Not quoted in window ({data.not_quoted_in_window.count} total, first 50 shown)
      </h3>
      <div class="tym-table-wrap tym-table-scroll max-h-[360px]">
        <table class="tym-table">
          <thead>
            <tr>
              <th>Product</th>
              <th class="w-[140px]">Category</th>
            </tr>
          </thead>
          <tbody>
            {#each data.not_quoted_in_window.results as p (p.id)}
              <tr>
                <td>
                  <div class="text-[13px] font-medium text-navy leading-tight">{p.name}</div>
                  <div class="text-[11px] text-dark-gray font-mono mt-0.5">{p.part_number}</div>
                </td>
                <td class="text-[12px] text-dark-gray">{p.category || '—'}</td>
              </tr>
            {:else}
              <tr><td class="empty" colspan="2">Every active product was quoted in this window.</td></tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>

    <div class="tym-card p-4 lg:col-span-2">
      <h3 class="mb-3 font-heading text-[13.5px] font-bold text-navy">Average margin by ownership</h3>
      <p class="tym-small mb-3">
        Per-row (list − cost) / list × 100, averaged — not a pooled figure. Only products with both prices and a
        positive list price count.
      </p>
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {#each data.margin_by_ownership as m (m.ownership ?? 'none')}
          <div class="tym-card p-3">
            <div class="tym-micro">{m.ownership ?? 'No ownership'}</div>
            <div class="font-heading text-xl font-bold tabular-nums text-navy">
              {m.avg_margin_percent.toFixed(1)}%
            </div>
          </div>
        {:else}
          <p class="tym-small">No products have both list and cost price set.</p>
        {/each}
      </div>
    </div>
  </div>
{/if}
